import { QueryClient, QueryKey } from '@tanstack/react-query';
import { queryKeys as k } from './queryKeys';

type JobScoped = { jobId: number };

/**
 * Every write in the app is one of these ops. The argument type is what the
 * mutation hook must supply so the map can build the exact keys to refresh.
 * `void` ops take no argument.
 */
export interface MutationArgs {
  'role.create': void;
  'role.update': void;
  'role.delete': void;
  'user.update': void;
  'user.invite': void;

  'office.create': void;
  'office.update': { id: number };
  'office.delete': void;
  'contact.save': void;
  'contact.delete': void;
  'costCode.create': void;
  'costCode.update': void;
  'costCode.delete': void;
  'payCode.save': void;
  'classification.save': void;
  'userRate.update': void;
  'divisions.sync': void;
  'apiConnection.save': void;
  'proact.update': void;
  'azureGroupSync.save': void;
  'scim.save': void;
  'backgroundJobs.requeue': void;
  'backgroundJobs.delete': void;
  'backgroundJobs.trigger': void;

  'documentTemplate.save': void;
  'documentTemplate.delete': void;
  'milestoneTemplate.save': void;
  'milestoneTemplate.delete': void;
  'milestonePhase.save': { jobId?: number; templateId?: string };
  'milestoneItem.save': JobScoped;
  'milestoneItem.saveForTemplate': { templateId?: string };

  'job.create': void;
  'job.update': JobScoped;
  'job.archive': void;
  'job.team.change': JobScoped;
  'job.workflows.update': JobScoped & { divisionId?: number };
  'job.syncCtc': JobScoped & { jobNumber: string };
  'hyperlink.save': JobScoped;
  'dailyProgress.create': JobScoped;

  'change.save': JobScoped;
  'changeType.save': void;
  'changeTemplate.save': void;
  'rfi.save': JobScoped;
  'submittal.save': JobScoped;
  'drawing.save': JobScoped;
  'transmittal.save': JobScoped;
  'workflowEntity.attachment': JobScoped;
  'workflowEntity.attachFromDocument': JobScoped;
  'workflowTemplate.save': void;
  'workflow.update': JobScoped;
  'workflowNotification.send': {
    resourceType: string;
    jobId: number;
    entityId: string;
  };

  'document.write': JobScoped;
  'folder.write': JobScoped;
  'post.write': JobScoped;
  'postComment.write': JobScoped & { postId: string };
  'userNotification.read': void;
  'meeting.save': JobScoped;
  'meeting.publish': JobScoped & { meetingId: string };
  'meetingTopic.save': JobScoped & { meetingId: string };
  'meetingIssue.save': JobScoped & { meetingId: string };
  'meetingComment.save': JobScoped & { meetingId: string; issueId: string };
  'meetingType.create': JobScoped;
  'question.save': JobScoped;

  'timesheet.write': void;
  'timesheet.externalReview': void;
  'dailyAttachment.comment': void;
  'daily.write': JobScoped;
  'workTicket.save': JobScoped;
  'jobRate.create': JobScoped;

  'wbs.save': JobScoped;
  'wbsAccount.save': JobScoped;
  'wbsAdjustment.save': JobScoped & { accountId: string };
  'designModel.create': JobScoped;
  'designModel.update': JobScoped & { id: string };
  'designModel.delete': JobScoped;
  'designModel.reprocess': JobScoped & { id: string };
  'designModel.grouping': JobScoped & { id: string };
  'progressItem.save': JobScoped;
  'ctcAdjustment.create': { ctcJobId: string };

  'knowledgeArticle.create': void;
  'knowledgeArticle.update': void;
  'knowledgeArticle.archive': void;
  'knowledgeArticle.restore': void;

  'chat.conversation.write': void;
  'chat.messageFeedback.write': void;
  'chat.memory.write': void;
  'chatAdmin.userMemory.delete': { userId: number };
}

export type MutationOp = keyof MutationArgs;

// A target is a bare key, or a key paired with the refetch behaviour to use
// when the write already seeded fresh data under it.
type InvalidationTarget =
  | QueryKey
  | { queryKey: QueryKey; refetchType: 'none' };

type KeyList = readonly InvalidationTarget[];

// Every screen that renders a job's workflow records also renders the job
// summary card and the job DTO's embedded workflow state.
const jobWorkflowKeys = ({ jobId }: JobScoped): KeyList => [
  k.jobs.summary(jobId),
  k.jobs.detail(jobId),
  k.posts.forJob(jobId),
];

const workflowEntityKeys = ({ jobId }: JobScoped): KeyList => [
  k.changes.forJob(jobId),
  k.rfis.forJob(jobId),
  k.submittals.forJob(jobId),
  k.drawings.forJob(jobId),
  k.transmittals.forJob(jobId),
];

// Workflow document pickers cache their own view of the job document tree.
const workflowDocumentOptionKeys = ({ jobId }: JobScoped): KeyList => [
  k.changes.documentOptions(jobId),
  k.rfis.documentOptions(jobId),
  k.submittals.documentOptions(jobId),
  k.drawings.documentOptions(jobId),
  k.transmittals.documentOptions(jobId),
];

export const invalidationMap: {
  [Op in MutationOp]: (args: MutationArgs[Op]) => KeyList;
} = {
  // A role's scopes drive feature gating, which every screen reads off the
  // signed-in user's cached profile.
  'role.create': () => [k.organization.roles(), k.users.all()],
  'role.update': () => [k.organization.roles(), k.users.all()],
  'role.delete': () => [
    k.organization.roles(),
    k.organization.adminUsers(),
    k.users.all(),
  ],

  // A user write is visible in the org list, every job team, the user
  // directory, and the timesheet office rosters. Archive and unarchive also
  // change which users those lists contain.
  'user.update': () => [
    k.organization.adminUsers(),
    k.users.all(),
    k.jobs.all(),
    k.timesheets.usersForOfficeAll(),
  ],
  // Inviting an archived user's email restores that user, which adds them
  // back to every job team and office roster.
  'user.invite': () => [
    k.organization.adminUsers(),
    k.users.list(),
    k.jobs.all(),
    k.timesheets.usersForOfficeAll(),
  ],

  // Office writes can add or rename divisions and change per-office workflow options.
  'office.create': () => [
    k.organization.offices(),
    k.organization.divisions(),
    k.organization.summaries(),
  ],
  'office.update': ({ id }) => [
    k.organization.offices(),
    k.organization.officeDetail(id),
    k.organization.divisions(),
    k.organization.summaries(),
    k.workflows.all(),
  ],
  'office.delete': () => [
    k.organization.offices(),
    k.organization.divisions(),
    k.organization.summaries(),
    k.workflows.all(),
  ],
  'contact.save': () => [k.organization.contacts()],
  'contact.delete': () => [k.organization.contacts()],
  'costCode.create': () => [k.costCodes.list()],
  'costCode.update': () => [k.costCodes.list()],
  'costCode.delete': () => [k.costCodes.list()],
  // Pay codes, classifications and rates feed timesheet rate previews.
  'payCode.save': () => [k.payCodes.list(), k.timesheets.all()],
  'classification.save': () => [k.classifications.list(), k.timesheets.all()],
  'userRate.update': () => [k.userRates.list(), k.timesheets.all()],
  'divisions.sync': () => [k.organization.divisions()],
  // Proact settings and SCIM integrations display the connection they reference.
  'apiConnection.save': () => [
    k.organization.apiConnections(),
    k.proact.settings(),
    k.scim.integrations(),
  ],
  'proact.update': () => [k.proact.settings()],
  'azureGroupSync.save': () => [k.azureGroupSync.all()],
  'scim.save': () => [k.scim.all()],
  // A job write moves it between states, so the counts and the paged lists
  // both go stale; the server roster does not. Triggering additionally restamps
  // the recurring schedule's last/next execution.
  'backgroundJobs.requeue': () => [
    k.backgroundJobs.overview(),
    k.backgroundJobs.jobsAll(),
  ],
  'backgroundJobs.delete': () => [
    k.backgroundJobs.overview(),
    k.backgroundJobs.jobsAll(),
  ],
  'backgroundJobs.trigger': () => [
    k.backgroundJobs.overview(),
    k.backgroundJobs.jobsAll(),
    k.backgroundJobs.recurring(),
  ],

  'documentTemplate.save': () => [k.documentTemplates.all()],
  'documentTemplate.delete': () => [k.documentTemplates.all()],
  'milestoneTemplate.save': () => [
    k.milestoneTemplates.all(),
    k.milestonePhases.all(),
  ],
  'milestoneTemplate.delete': () => [k.milestoneTemplates.all()],
  // The job DTO carries its active phase, so the job list and the CTC linked
  // jobs show the phase title.
  'milestonePhase.save': ({ jobId, templateId }) => [
    ...(jobId != null
      ? [
          k.milestonePhases.forJob(jobId),
          k.milestoneItems.forJob(jobId),
          k.jobs.detail(jobId),
          k.jobs.list(),
          k.ctc.all(),
        ]
      : []),
    ...(templateId != null
      ? [
          k.milestonePhases.forTemplate(templateId),
          k.milestoneTemplates.detail(templateId),
        ]
      : []),
  ],
  'milestoneItem.save': ({ jobId }) => [
    k.milestoneItems.forJob(jobId),
    k.milestonePhases.forJob(jobId),
    k.jobs.summary(jobId),
  ],
  'milestoneItem.saveForTemplate': ({ templateId }) => [
    k.milestoneTemplates.all(),
    ...(templateId != null ? [k.milestonePhases.forTemplate(templateId)] : []),
  ],

  'job.create': () => [
    k.jobs.list(),
    k.organization.summaries(),
    k.ctc.linkedJobs(),
    k.ctc.all(),
  ],
  // A job write changes the name and number rendered by every job-scoped
  // view, including search results and the CTC linked-jobs table.
  'job.update': () => [k.jobs.all(), k.ctc.linkedJobs()],
  'job.archive': () => [k.jobs.all()],
  // The job DTO embeds the team, so the whole detail subtree refreshes.
  'job.team.change': ({ jobId }) => [k.jobs.detail(jobId)],
  'job.workflows.update': ({ jobId, divisionId }) => [
    k.jobs.detail(jobId),
    k.workflows.forJob(jobId),
    ...(divisionId != null ? [k.workflows.forDivision(divisionId)] : []),
  ],
  'job.syncCtc': ({ jobId, jobNumber }) => [
    k.jobs.detail(jobId),
    k.ctc.job(jobNumber),
  ],
  // The job DTO embeds its hyperlinks, so the summary page shows them too.
  'hyperlink.save': ({ jobId }) => [
    k.jobs.hyperlinks(jobId),
    k.jobs.detail(jobId),
  ],
  'dailyProgress.create': ({ jobId }) => [
    k.dailyProgress.history(jobId),
    k.wbs.forJob(jobId),
    k.jobs.summary(jobId),
  ],

  'change.save': ({ jobId }) => [
    k.changes.forJob(jobId),
    k.changes.overview(),
    ...jobWorkflowKeys({ jobId }),
  ],
  'changeType.save': () => [k.changeTypes.all()],
  'changeTemplate.save': () => [k.organization.changeTemplates()],
  'rfi.save': ({ jobId }) => [
    k.rfis.forJob(jobId),
    k.rfis.overview(),
    ...jobWorkflowKeys({ jobId }),
  ],
  'submittal.save': ({ jobId }) => [
    k.submittals.forJob(jobId),
    ...jobWorkflowKeys({ jobId }),
  ],
  'drawing.save': ({ jobId }) => [
    k.drawings.forJob(jobId),
    ...jobWorkflowKeys({ jobId }),
  ],
  'transmittal.save': ({ jobId }) => [
    k.transmittals.forJob(jobId),
    ...jobWorkflowKeys({ jobId }),
  ],
  'workflowEntity.attachment': (args) => workflowEntityKeys(args),
  'workflowEntity.attachFromDocument': (args) => [
    ...workflowEntityKeys(args),
    ...workflowDocumentOptionKeys(args),
    k.documents.forJob(args.jobId),
  ],
  'workflowTemplate.save': () => [k.workflows.all()],
  'workflow.update': ({ jobId }) => [
    k.workflows.forJob(jobId),
    k.jobs.detail(jobId),
  ],
  'workflowNotification.send': ({ resourceType, jobId, entityId }) => [
    k.workflowNotifications.history(resourceType, jobId, entityId),
  ],

  // Any document-tree write refreshes the whole job tree plus the
  // workflow pickers and post pickers that read it.
  'document.write': ({ jobId }) => [
    k.documents.forJob(jobId),
    ...workflowDocumentOptionKeys({ jobId }),
    k.posts.forJob(jobId),
  ],
  'folder.write': ({ jobId }) => [
    k.documents.forJob(jobId),
    ...workflowDocumentOptionKeys({ jobId }),
    k.posts.forJob(jobId),
  ],
  // Posts appear in the job feed, the related-record footers, the global
  // activity page, and the assigned-to-me lists.
  // The job summary embeds the due and constraint posts.
  'post.write': ({ jobId }) => [
    k.posts.forJob(jobId),
    k.posts.activityFeed(),
    k.posts.allAssigned(),
    k.userNotifications.all(),
    k.jobs.summary(jobId),
  ],
  'postComment.write': ({ jobId, postId }) => [
    k.posts.comments(postId),
    k.posts.detail(jobId, postId),
    k.posts.forJob(jobId),
    k.posts.activityFeed(),
    k.posts.allAssigned(),
  ],
  'userNotification.read': () => [k.userNotifications.all()],
  'meeting.save': ({ jobId }) => [k.meetings.forJob(jobId)],
  'meeting.publish': ({ jobId, meetingId }) => [
    k.meetings.forJob(jobId),
    k.documents.forJob(jobId),
    k.workflowNotifications.history('meeting', jobId, meetingId),
  ],
  'meetingTopic.save': ({ jobId, meetingId }) => [
    k.meetings.detail(jobId, meetingId),
    k.meetings.list(jobId),
  ],
  'meetingIssue.save': ({ jobId, meetingId }) => [
    k.meetings.detail(jobId, meetingId),
    k.meetings.issues(jobId),
  ],
  'meetingComment.save': ({ jobId, meetingId, issueId }) => [
    k.meetings.comments(issueId),
    k.meetings.detail(jobId, meetingId),
    k.meetings.issues(jobId),
  ],
  'meetingType.create': ({ jobId }) => [k.jobs.meetingTypes(jobId)],
  'question.save': ({ jobId }) => [k.questions.forJob(jobId)],

  // Timesheet writes change office summaries, admin tabs, missing lists,
  // audit and payroll reports and week-hour panels. Rate previews key off
  // their own params, so a save cannot change them. The detail key holds the
  // server response the mutation seeded, so it is marked stale but not
  // refetched.
  'timesheet.write': () => [
    k.timesheets.forJobAll(),
    k.timesheets.adminListAll(),
    k.timesheets.summaryAll(),
    k.timesheets.missingAll(),
    k.timesheets.weekHoursAll(),
    k.timesheets.auditReportAll(),
    k.timesheets.payrollReportAll(),
    { queryKey: k.timesheets.detailAll(), refetchType: 'none' },
  ],
  'timesheet.externalReview': () => [k.timesheets.all()],
  'dailyAttachment.comment': () => [k.dailys.all()],
  'daily.write': ({ jobId }) => [
    k.dailys.all(),
    k.posts.forJob(jobId),
    k.jobs.summary(jobId),
  ],
  'workTicket.save': ({ jobId }) => [k.workTickets.forJob(jobId)],
  'jobRate.create': ({ jobId }) => [k.rates.forJob(jobId), k.timesheets.all()],

  'wbs.save': ({ jobId }) => [k.wbs.forJob(jobId), k.jobs.summary(jobId)],
  'wbsAccount.save': ({ jobId }) => [
    k.wbs.forJob(jobId),
    k.jobs.summary(jobId),
    k.designModels.laborAccounts(jobId),
  ],
  'wbsAdjustment.save': ({ jobId, accountId }) => [
    k.wbs.adjustments(accountId),
    k.wbs.forJob(jobId),
    k.jobs.summary(jobId),
  ],
  'designModel.create': ({ jobId }) => [
    k.designModels.list(jobId),
    k.jobs.detail(jobId),
  ],
  'designModel.update': ({ jobId, id }) => [
    k.designModels.list(jobId),
    k.designModels.detail(id),
    k.designModels.grouping(id),
    k.jobs.detail(jobId),
  ],
  'designModel.delete': ({ jobId }) => [
    k.designModels.list(jobId),
    k.jobs.detail(jobId),
  ],
  'designModel.reprocess': ({ jobId, id }) => [
    k.designModels.list(jobId),
    k.designModels.detail(id),
    k.designModels.ifc(id),
    k.designModels.grouping(id),
  ],
  'designModel.grouping': ({ jobId, id }) => [
    k.designModels.grouping(id),
    k.designModels.list(jobId),
  ],
  // Progress statuses roll into WBS totals, the model list's progress columns
  // and the daily design progress summaries (un-installing an item drops it
  // from a summary whose progress item id list is unchanged).
  'progressItem.save': ({ jobId }) => [
    k.wbs.forJob(jobId),
    k.designModels.list(jobId),
    k.jobs.summary(jobId),
    k.dailys.designProgressSummaryForJob(jobId),
  ],
  'ctcAdjustment.create': ({ ctcJobId }) => [k.ctc.job(ctcJobId), k.wbs.all()],

  'knowledgeArticle.create': () => [k.knowledgeArticles.all()],
  'knowledgeArticle.update': () => [k.knowledgeArticles.all()],
  'knowledgeArticle.archive': () => [k.knowledgeArticles.all()],
  'knowledgeArticle.restore': () => [k.knowledgeArticles.all()],

  'chat.conversation.write': () => [k.chat.conversations()],
  'chat.messageFeedback.write': () => [k.chatAdmin.all()],
  'chat.memory.write': () => [k.chat.memory()],
  // An admin may delete one of their own notes, which their own list also shows.
  'chatAdmin.userMemory.delete': ({ userId }) => [
    k.chatAdmin.userMemory(userId),
    k.chat.memory(),
  ],
};

type InvalidateRest<Op extends MutationOp> = MutationArgs[Op] extends void
  ? []
  : [args: MutationArgs[Op]];

export function invalidateFor<Op extends MutationOp>(
  op: Op,
  qc: QueryClient,
  ...rest: InvalidateRest<Op>
) {
  const build = invalidationMap[op] as (args: MutationArgs[Op]) => KeyList;
  const keys = build(rest[0] as MutationArgs[Op]);
  return Promise.all(
    keys.map((target) =>
      qc.invalidateQueries(
        Array.isArray(target)
          ? { queryKey: target as QueryKey }
          : (target as { queryKey: QueryKey; refetchType: 'none' }),
      ),
    ),
  );
}
